import React from "react"
import {compose} from "redux"
import {useTranslation} from "react-i18next"
import {RouteComponentProps, withRouter} from "react-router"

// HOCs
import withRecords, {WithRecordsProps} from "../../../hoc/withRecords"
import withConclusion, {WithConclusionProps} from "../../../hoc/withConclusion"
import withApproveDialog, {WithApproveDialogProps} from "../../../hoc/withApproveDialog"
import withRecordApprove, {WithRecordApproveProps} from "../../../hoc/withRecordApprove"
import withInterpretation, {WithInterpretationProps} from "../../../hoc/withInterpretation"

// Components
import ModernDialog from "../../../../../components/modern/ModernDialog"
import ModernButton from "../../../../../components/modern/ModernButton"

// Constants
import {AppRoute} from "../../../../routing/constants"

// Styles
import useStyles from "./styles"

type ModernApproveDialogProps =
    RouteComponentProps &
    WithApproveDialogProps &
    WithRecordsProps &
    WithRecordApproveProps &
    WithConclusionProps &
    WithInterpretationProps

/**
 * Modern equivalent of <ApproveDialog/>.
 *
 * Same store wiring, same HOC stack and the same approval call — what changes
 * is the presentation: MUI's Dialog, type scale and buttons are replaced by
 * <ModernDialog/> and <ModernButton/>, so the last confirmation before a
 * record is signed stops looking like a different application's window.
 *
 * Two behaviours are carried over deliberately, because the flow depends on
 * them and this component is not the place to change them:
 *
 * 1. The `history.replace` below runs DURING RENDER, exactly as Classic's does.
 *    Approving without "receive next" nulls the current record in the same
 *    commit, and the redirect has to win that race or the reviewer is left on
 *    the "nothing to review" screen. This is why <ModernRecordPage/> keeps the
 *    dialog mounted for the whole route and why that tree must never be wrapped
 *    in StrictMode.
 * 2. The dialog snapshots the conclusion and interpretation when it opens, so
 *    the text being confirmed cannot change underneath the reviewer while an
 *    autosave lands.
 *
 * ONE behaviour is deliberately not carried over: Classic raises a browser
 * `alert("Interpretation is blank.")` from a layout effect. Here that is an
 * inline warning row instead — same information, next to the empty field,
 * without an OS dialog on top of the dialog. Classic's alert is untouched.
 */
function ModernApproveDialog(props: ModernApproveDialogProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const {
        approveDialogActive,
        setApproveDialogActive,
        receiveNext,
        setReceiveNext,
        currentApproving,
        approveRecord,
        currentRecord,
        conclusion,
        interpretation,
        history,
        recordApproved,
    } = props;

    const [shownConclusion, setShownConclusion] = React.useState<string | null>(null);
    const [shownInterpretation, setShownInterpretation] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (approveDialogActive) {
            setShownConclusion(conclusion);
            setShownInterpretation(interpretation)
        }
    }, [approveDialogActive, conclusion, interpretation]);

    // see note 1 in the component doc — render phase on purpose
    if (recordApproved && !receiveNext) {
        history.replace(AppRoute.Home)
    }

    if (!approveDialogActive) {
        return null
    }

    const displayConclusion = shownConclusion !== null ? shownConclusion : conclusion;
    const displayInterpretation = shownInterpretation !== null ? shownInterpretation : interpretation;
    const interpretationBlank =
        displayInterpretation === undefined ||
        displayInterpretation === null ||
        String(displayInterpretation).trim() === "";

    function close() {
        setApproveDialogActive(false)
    }

    function renderValue(value: string | null | undefined) {
        const text = value === undefined || value === null ? "" : String(value);

        if (text.trim() === "") {
            return <span className={`${styles.value} ${styles.value__empty}`}>{t("modern.not_provided")}</span>
        }

        return <span className={styles.value}>{text}</span>
    }

    return (
        <ModernDialog
            open={approveDialogActive}
            title={t("approve_modal.title")}
            onClose={close}
            actions={
                <React.Fragment>
                    <ModernButton onClick={close}>{t("cancel")}</ModernButton>
                    <ModernButton
                        variant="ok"
                        loading={currentApproving}
                        onClick={() => {
                            if (currentRecord !== null) {
                                approveRecord(currentRecord.teleRecordId)
                            }
                        }}
                    >
                        {t("approve")}
                    </ModernButton>
                </React.Fragment>
            }
        >
            {interpretationBlank && (
                <div className={styles.warn} role="alert">
                    {t("modern.interpretation_blank")}
                </div>
            )}

            <div className={styles.block}>
                <span className={styles.label}>{t("records.conclusion")}</span>
                {renderValue(displayConclusion)}
            </div>

            <div className={styles.block}>
                <span className={styles.label}>{t("interpretation.title")}</span>
                {renderValue(displayInterpretation)}
            </div>

            <label className={styles.check}>
                <input
                    type="checkbox"
                    checked={receiveNext}
                    onChange={() => setReceiveNext(!receiveNext)}
                />
                {t("approve_modal.automatically_next")}
            </label>
        </ModernDialog>
    )
}

export {ModernApproveDialog}

export default compose(
    withConclusion,
    withInterpretation,
    withApproveDialog,
    withRecordApprove,
    withRecords,
    withRouter,
)(ModernApproveDialog) as React.ComponentType<any>
